import { useEffect, useLayoutEffect, useState } from "react";
import { isSafari } from "react-device-detect";
import { Flex, Text, useToast } from "@chakra-ui/react";
import { Squiggle170x10Icon } from "../../components/icons/Squiggle170x10Icon";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  EventContextArea,
} from "../../components/funnel/types";
import { useGetNavigationContext } from "../../hooks/useGetNavigationContext";
import { useAccountData } from "../../hooks/useAccountData";
import { useContentFunnel } from "../../components/funnel/hooks/useContentFunnel";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints";
import { t } from "i18next";
import { ValkyraeFooter } from "../valkyrae/ValkyraeFooter";
import { useNavigate } from "react-router";
import { Trans } from "react-i18next";
import { UltimateXmenBundleHero } from "./UltimateXmenBundleHero";
import { YearlyBundleReleasesGrid } from "../../components/cards/YearlyBundleReleasesGrid";

export function UltimateXmenBundleScreen() {
  const isMobileBreakpoint = useMobileBreakpoint();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const accountData = useAccountData();
  const navigate = useNavigate();
  const [funnelConfig, setFunnelConfig] = useState<
    FunnelConfigTypes | undefined
  >({
    type: FUNNEL_TYPES.subscription,
  });

  useLayoutEffect(() => {
    if (accountData && !accountData.features[`yearly_bundle`]) {
      navigate("/", { replace: true });
    }
  }, [accountData]);

  const focusPayment = () => {
    const container = document.querySelector(
      "[data-payment-section]",
    ) as HTMLElement;

    if (!container) {
      console.warn("Payment container element not found.");
      return; // Exit if the element isn't there
    }

    if (isSafari) {
      setTimeout(() => {
        container.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 500); // Increased timeout to 500ms
    } else {
      // Standard behavior for other browsers
      container.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <Flex direction="column" width="100%">
      <Flex
        as="main"
        role="main"
        position="relative"
        width="100%"
        flexDirection="column"
        paddingBottom="40px"
        alignItems="center"
        backgroundImage={`url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hero_glow_v2.svg)`}
        backgroundRepeat="no-repeat"
        backgroundPosition={isMobileBreakpoint ? "center -23%" : "center -33px"}
        overflowX="hidden"
      >
        <UltimateXmenBundleHero onGetDealClick={focusPayment} />
        <Flex
          flexDirection="column"
          width="100%"
          maxWidth="964px"
          alignItems="center"
        >
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="center"
            gap="24px"
          >
            {/* Paywall Section */}
            {isMobileBreakpoint && (
              <Flex
                width="100%"
                px={isMobileBreakpoint ? "20px" : "0"}
                pb="32px"
              >
                <Flex
                  data-payment-section
                  padding={isSmallBreakpoint ? "32px 24px" : "64px 32px"}
                  background="charcoal.700"
                  borderRadius="24px"
                  width="100%"
                  justifyContent="center"
                  mt="-100px"
                >
                  <Flex maxWidth="512px" width="100%" justifyContent="center">
                    <UltimateXmenBundlePayment
                      contextArea={FunnelContextArea.holidayBundle}
                      title={t("screens.ultimateXmenBundle.becomeAGoldMember")}
                      funnelConfig={funnelConfig}
                      setFunnelConfig={setFunnelConfig}
                    />
                  </Flex>
                </Flex>
              </Flex>
            )}

            <Text
              color="white"
              textAlign="center"
              fontFamily="Bricolage Grotesque"
              fontSize={isMobileBreakpoint ? "32px" : "45px"}
              fontWeight={isMobileBreakpoint ? "600" : "800"}
              lineHeight="107%"
              marginTop={isMobileBreakpoint ? undefined : "-30px"}
              px={isMobileBreakpoint ? "20px" : "0"}
            >
              <Trans
                i18nKey="screens.ultimateXmenBundle.getTheUltimateXMenBundle"
                components={{ 1: <br /> }}
              />
            </Text>
            <Squiggle170x10Icon
              width="170px"
              height="10px"
              color="blaze.blaze"
            />
            <Text
              color="dune.600"
              textAlign="center"
              fontSize="18px"
              maxWidth="800px"
              fontWeight="500"
              px="60px"
            >
              <Trans
                i18nKey="screens.ultimateXmenBundle.goAnnualForLimitedTime"
                components={{ 1: <br /> }}
              />
            </Text>
          </Flex>

          {/* Release Cards Grid */}
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="center"
            mt="40px"
            mb="32px"
          >
            <YearlyBundleReleasesGrid
              sectionKeyProd="2e06b68f-6f96-47bc-8e13-548c606d7bbf"
              sectionKeyDev="6048f1fb-0243-4578-9fbe-313791242427"
              queryKeyName="ultimateXmenBundleReleases"
            />
          </Flex>

          {/* Paywall Section */}
          {!isMobileBreakpoint && (
            <Flex width="100%" px={isMobileBreakpoint ? "20px" : "0"} pb="32px">
              <Flex
                data-payment-section
                padding={isSmallBreakpoint ? "32px 24px" : "64px 32px"}
                background="charcoal.700"
                borderRadius="24px"
                width="100%"
                justifyContent="center"
                mt="40px"
              >
                <Flex maxWidth="512px" width="100%" justifyContent="center">
                  <UltimateXmenBundlePayment
                    contextArea={FunnelContextArea.holidayBundle}
                    title={t("screens.ultimateXmenBundle.becomeAGoldMember")}
                    funnelConfig={funnelConfig}
                    setFunnelConfig={setFunnelConfig}
                  />
                </Flex>
              </Flex>
            </Flex>
          )}

          <ValkyraeFooter isMobileBreakpoint={isMobileBreakpoint} />
        </Flex>
      </Flex>
    </Flex>
  );
}

export function UltimateXmenBundlePayment({
  contextArea,
  title,
  funnelConfig,
  setFunnelConfig,
}: {
  contextArea: FunnelContextArea;
  title?: string | JSX.Element;
  funnelConfig: FunnelConfigTypes | undefined;
  setFunnelConfig: (config: FunnelConfigTypes | undefined) => void;
}) {
  const accountData = useAccountData();

  // Reset funnel when user logs out
  useEffect(() => {
    if (!accountData?.user) {
      setFunnelConfig({ type: FUNNEL_TYPES.subscription });
    }
  }, [accountData?.user, setFunnelConfig]);

  const navigate = useNavigate();
  const createToast = useToast();
  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.blackFridayPage,
  });

  const isCongratsStep = funnelConfig?.type === FUNNEL_TYPES.congrats;

  const subscriptionProps = {
    displayExploreMoreContent: false,
    hideValueProps: true,
    customHeader: title,
    customSubmitButtonText: t("screens.ultimateXmenBundle.signUpNow"),
    centerWelcomeText: true,
    largeLoginCTA: true,
    hidePromoCode: true,
    showYearlyOnly: true,
  };

  const { funnelBody } = useContentFunnel({
    setFunnelConfig,
    onCloseFunnel: () => setFunnelConfig(undefined),
    openFunnelType: funnelConfig?.type,
    context: { modalContext: contextArea, navigationContext },

    options: {
      componentProps: isCongratsStep
        ? funnelConfig?.options?.componentProps
        : subscriptionProps,
      onSuccess: (result) => {
        if (isCongratsStep) {
          // User dismissed congrats, navigate home
          navigate("/");
          createToast({
            status: "success",
            description: t("screens.gold.goldSubscriptionActivated"),
            duration: 5000,
          });
        } else {
          // Subscription purchased, show congrats inline
          setFunnelConfig({
            type: FUNNEL_TYPES.congrats,
            context: result.context,
            options: {
              ...result.options,
            },
          });
        }
      },
    },
  });

  return funnelBody;
}
